import { useState, useRef } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Upload, FileText, Download, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

const ImportDialog = ({ 
  open, 
  onOpenChange, 
  importType, // 'players' | 'events' | 'music'
  onSuccess 
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const titles = {
    players: "Import Players",
    events: "Import Events",
    music: "Import Music",
    inventory: "Import Inventory",
    attendance: "Import Attendance"
  };

  const descriptions = {
    players: "Upload a CSV file. Required: name, email. Optional: phone, address, section (primary), secondary_section, player_type, jacket_size, shirt_size, medical_info, dietary_info, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship.",
    events: "Upload a CSV file with columns: title, event_type, date, start_time, end_time, arrival_time, location, details, is_private, status. Existing events with the same title + date will be UPDATED in place.",
    music: "Upload a CSV file with columns: number, title, composer, arranger, genre, solo, comments. Existing pieces with the same title + composer will be UPDATED in place.",
    inventory: "Upload a CSV file with columns: category (instrument/uniform/accessory), subcategory, name, asset_tag, make, finish, serial_number, size, condition, purchase_date, purchase_price, supplier, notes",
    attendance: "One row per (event, player, status). Required: event_date (YYYY-MM-DD), player_email, status. Optional: event_title (to disambiguate same-date events), notes. Status accepts attending / not_attending / maybe / pending and friendly aliases (yes/y, no/n, m, ?)."
  };

  const columnHeaders = {
    players: ["Name", "Email", "Phone", "Section", "Secondary", "Type"],
    events: ["Title", "Type", "Date", "Start", "End", "Location", "Status"],
    music: ["Number", "Title", "Composer", "Arranger", "Genre", "Solo"],
    inventory: ["Category", "Subcategory", "Name", "Asset Tag", "Make"],
    attendance: ["Date", "Event", "Player", "Status"]
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Attendance allows XLSX (wide-format template) as well as CSV.
    const allowedXlsx = importType === 'attendance';
    const lname = selectedFile.name.toLowerCase();
    const isCsv = lname.endsWith('.csv');
    const isXlsx = lname.endsWith('.xlsx') || lname.endsWith('.xls');
    if (!(isCsv || (allowedXlsx && isXlsx))) {
      toast.error(allowedXlsx ? "Please select a CSV or XLSX file" : "Please select a CSV file");
      return;
    }

    setFile(selectedFile);
    setLoading(true);
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const res = await apiClient.post(`/import/${importType}?preview_only=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await apiClient.post(`/import/${importType}?preview_only=false`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const created = res.data.imported_count || 0;
      const updated = res.data.updated_count || 0;
      if (created > 0 || updated > 0) {
        const parts = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        // iter-80: imports no longer auto-send welcome emails for players.
        // Show a longer, sticky toast so admins know they need to send.
        if (importType === "players" && created > 0) {
          toast.success(
            `Import successful — ${parts.join(", ")}. Welcome emails NOT sent. Use 'Send Welcomes' on the Players page to email login details.`,
            { duration: 8000 }
          );
        } else {
          toast.success(`Import successful — ${parts.join(", ")}`);
        }
        onSuccess?.();
        handleClose();
      } else {
        toast.error("No records were imported. Check the errors below.");
        setPreview(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiClient.get(`/import/templates/${importType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${importType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // iter-61: pre-filled XLSX attendance template (event titles + player names already in).
  const handleDownloadXlsxTemplate = async () => {
    try {
      const res = await apiClient.get(`/import/templates/attendance.xlsx`, {
        responseType: 'blob'
      });
      const cd = res.headers?.['content-disposition'] || res.headers?.get?.('content-disposition') || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : 'attendance_template.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download XLSX template");
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  const renderPreviewRow = (item, index) => {
    if (importType === 'players') {
      return (
        <TableRow key={index}>
          <TableCell className="font-medium">{item.name}</TableCell>
          <TableCell>{item.email}</TableCell>
          <TableCell>{item.phone || '-'}</TableCell>
          <TableCell>{item.section || '-'}</TableCell>
          <TableCell>{item.secondary_section || '-'}</TableCell>
          <TableCell>
            <Badge variant="outline" className="text-xs">{item.player_type}</Badge>
          </TableCell>
        </TableRow>
      );
    }
    if (importType === 'events') {
      return (
        <TableRow key={index}>
          <TableCell className="font-medium">{item.title}</TableCell>
          <TableCell>
              <Badge variant="outline" className={`text-xs ${
                item.event_type === 'concert' ? 'border-red-500/30 text-red-400'
                  : item.event_type === 'contest' ? 'border-purple-400/30 text-purple-300'
                  : 'border-blue-500/30 text-blue-400'
              }`}>
              {item.event_type}
            </Badge>
          </TableCell>
          <TableCell>{item.date}</TableCell>
          <TableCell>{item.start_time}</TableCell>
          <TableCell>{item.end_time}</TableCell>
          <TableCell>{item.location}</TableCell>
          <TableCell>
            <Badge variant="outline" className={`text-xs ${
              item.status === 'confirmed' ? 'border-green-500/30 text-green-400' :
              item.status === 'provisional' ? 'border-yellow-500/30 text-yellow-400' :
              'border-red-500/30 text-red-400'
            }`}>
              {item.status}
            </Badge>
          </TableCell>
        </TableRow>
      );
    }
    if (importType === 'music') {
      return (
        <TableRow key={index}>
          <TableCell className="font-mono text-xs">{item.number || '-'}</TableCell>
          <TableCell className="font-medium">{item.title}</TableCell>
          <TableCell>{item.composer}</TableCell>
          <TableCell>{item.arranger || '-'}</TableCell>
          <TableCell>{item.genre || '-'}</TableCell>
          <TableCell>{item.solo || '-'}</TableCell>
        </TableRow>
      );
    }
    if (importType === 'inventory') {
      return (
        <TableRow key={index}>
          <TableCell>
            <Badge variant="outline" className="text-xs capitalize">{item.category}</Badge>
          </TableCell>
          <TableCell>{item.subcategory || '-'}</TableCell>
          <TableCell className="font-medium">{item.name}</TableCell>
          <TableCell className="font-mono text-xs">{item.asset_tag || '-'}</TableCell>
          <TableCell>{item.make || '-'}</TableCell>
        </TableRow>
      );
    }
    if (importType === 'attendance') {
      const statusColor = {
        attending: 'border-green-500/30 text-green-400',
        not_attending: 'border-red-500/30 text-red-400',
        maybe: 'border-yellow-500/30 text-yellow-400',
        pending: 'border-white/20 text-muted-foreground',
      }[item.status] || 'border-white/20 text-muted-foreground';
      return (
        <TableRow key={index}>
          <TableCell className="font-mono text-xs">{item.event_date}</TableCell>
          <TableCell className="font-medium">{item.event_title || '-'}</TableCell>
          <TableCell>{item.player_name || item.player_email}</TableCell>
          <TableCell>
            <Badge variant="outline" className={`text-xs ${statusColor}`}>
              {item.status}
            </Badge>
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass border-white/[0.06] max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" />
            {titles[importType]}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {descriptions[importType]}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* File Upload Area */}
          <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-primary/30 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept={importType === 'attendance' ? '.csv,.xlsx,.xls' : '.csv'}
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {file ? file.name : (importType === 'attendance' ? "Click to upload CSV or XLSX" : "Click to upload CSV file")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {file ? `${(file.size / 1024).toFixed(1)} KB` : "or drag and drop"}
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* Download Template Button(s) */}
          <div className={importType === 'attendance' ? "grid grid-cols-2 gap-2" : ""}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="w-full border-white/[0.06]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV Template
            </Button>
            {importType === 'attendance' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadXlsxTemplate}
                className="w-full border-white/[0.06]"
                data-testid="download-attendance-xlsx-template"
              >
                <Download className="w-4 h-4 mr-2" />
                Download XLSX Template
              </Button>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Processing file...</span>
            </div>
          )}

          {/* Preview Results */}
          {preview && !loading && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/30">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm">
                    <strong>{preview.preview_count}</strong> records ready to import
                  </span>
                </div>
                {preview.errors?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-orange-400">
                      <strong>{preview.errors.length}</strong> errors
                    </span>
                  </div>
                )}
              </div>

              {/* Errors */}
              {preview.errors?.length > 0 && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <p className="text-sm font-medium text-red-400 mb-2">Errors found:</p>
                  <ul className="text-xs text-red-300 space-y-1 max-h-24 overflow-auto">
                    {preview.errors.map((error, i) => (
                      <li key={i}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview Table */}
              {preview.preview?.length > 0 && (
                <div className="rounded-xl border border-white/[0.06] overflow-hidden">
                  <div className="max-h-64 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          {columnHeaders[importType].map((header) => (
                            <TableHead key={header} className="text-xs font-semibold">{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.preview.map((item, index) => renderPreviewRow(item, index))}
                      </TableBody>
                    </Table>
                  </div>
                  {preview.preview_count > 50 && (
                    <p className="text-xs text-muted-foreground p-3 border-t border-white/[0.06] text-center">
                      Showing first 50 of {preview.preview_count} records
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
          <Button variant="ghost" onClick={handleClose} className="h-10">
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!preview || preview.preview_count === 0 || importing}
            className="bg-primary text-primary-foreground h-10"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import {preview?.preview_count || 0} Records
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
